/**
 * Pantalla unica del kiosco de Androcles.
 *
 * Disposicion fija en dos columnas (el kiosco no cambia de tamano):
 *  - Izquierda: avatar + estado del servicio. Siempre visible: da la sensacion
 *    de "hay alguien ahi" aunque el jefe no este hablando.
 *  - Derecha: una unica seccion cada vez, elegida con tres pestanas enormes.
 *
 * Nada de menus anidados, nada de iconos sin texto, nada de modales. Tres
 * destinos y vuelta: Hoy · La caja · Dictar.
 *
 * El dictado YA captura audio real: `lib/audio.ts` remuestrea el microfono a
 * PCM 16 kHz y lo manda por el WebSocket de `/api/voz/ws`.
 *
 * La conversacion de la seccion «Hoy» es HABLADA de ida y vuelta: el jefe
 * habla (o escribe), el orquestador responde por texto y esa respuesta se
 * REPRODUCE por voz (Piper TTS) que llega por el mismo socket como WAV. El
 * texto es el minimo garantizado: si la voz falla o esta silenciada, la
 * respuesta sigue viendose. Reproduccion en `lib/reproductor.ts`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Avatar, type EstadoAvatar } from '@/components/Avatar';
import { BandejaConfirmaciones } from '@/components/BandejaConfirmaciones';
import { Conversacion } from '@/components/Conversacion';
import { Dictado } from '@/components/Dictado';
import { Login } from '@/components/Login';
import { OndaVoz } from '@/components/OndaVoz';
import { PanelAjustes } from '@/components/PanelAjustes';
import {
  aplicarEscala,
  guardarEscala,
  leerEscala,
  escalaEfectiva,
  type Escala,
} from '@/lib/escala';
import { ParteDelDia } from '@/components/ParteDelDia';
import { PosicionPanel } from '@/components/PosicionPanel';
import { PrevisionChart } from '@/components/PrevisionChart';
import { Boton } from '@/components/ui/button';
import {
  alCaducarSesion,
  api,
  conectarConversacion,
  type Confirmacion,
  type ModoAcceso,
  type UsuarioSesion,
  type ControlConversacion,
  type CurvaPrevision,
  type EstadoServicio,
  type ParteDia,
  type Posicion,
  type ResultadoDictado,
  type SesionVoz,
  type Turno,
} from '@/lib/api';
import * as audio from '@/lib/audio';
import * as reproductor from '@/lib/reproductor';
import { cn } from '@/lib/utils';

/** Las tres unicas secciones del kiosco. */
type Seccion = 'hoy' | 'caja' | 'dictar';

/**
 * Marca de "ya recargue por sesion caducada" (vive lo que la pestana).
 * Evita que la recarga de limpieza se repita en bucle cuando el backend
 * sigue contestando 401 porque todavia no ha entrado nadie.
 */
const MARCA_RECARGA_CADUCIDAD = 'androcles_recarga_caducidad';

/**
 * Reintentos automaticos antes de avisar de que no hay linea. Cubre de sobra
 * un reinicio del backend (~40 s con las esperas 1+2+4+8+15).
 */
const MAX_REINTENTOS_LINEA = 5;

const SECCIONES: { clave: Seccion; etiqueta: string }[] = [
  { clave: 'hoy', etiqueta: 'Hoy' },
  { clave: 'caja', etiqueta: 'La caja' },
  { clave: 'dictar', etiqueta: 'Dictar' },
];

/**
 * Alterna entre pantalla grande y pantalla normal.
 *
 * Vive en dos sitios: la fila de pestanas y la pantalla de entrada. Lo segundo
 * NO es un adorno: la entrada se pinta encima de todo (`z-50`) y, sin este
 * boton ahi, con la sesion caducada no habria forma de reducir la pantalla
 * antes de escribir la contrasena —justo el problema que se venia a resolver—.
 */
function BotonTamano({
  tamano,
  alAlternar,
  className,
}: {
  tamano: 'kiosco' | 'normal';
  alAlternar: (escala: Escala) => void;
  className?: string;
}) {
  const esGrande = tamano === 'kiosco';
  return (
    <button
      type="button"
      onClick={() => alAlternar(esGrande ? 'normal' : 'kiosco')}
      title="Tamaño de la pantalla"
      aria-label={esGrande ? 'Ver la pantalla a tamaño normal' : 'Ver la pantalla a tamaño grande'}
      className={cn(
        'shrink-0 whitespace-nowrap rounded-md border border-border px-4 py-3 text-base',
        'font-medium text-muted-foreground transition-colors hover:bg-muted',
        'hover:text-foreground sm:text-lg',
        className,
      )}
    >
      {esGrande ? 'Reducir' : 'Agrandar'}
    </button>
  );
}

export default function App() {
  // --- Sesion -------------------------------------------------------------
  // El estado del kiosco (dictado a medias, conversacion, seccion abierta)
  // vive AQUI y no se desmonta cuando caduca la sesion: la pantalla de
  // entrada se pinta encima. Al volver a entrar, el jefe se encuentra
  // exactamente lo que estaba haciendo.
  const [acceso, setAcceso] = useState<ModoAcceso | null>(null);
  const [usuario, setUsuario] = useState<UsuarioSesion | null>(null);
  const [sesionCaducada, setSesionCaducada] = useState(false);

  const [seccion, setSeccion] = useState<Seccion>('hoy');
  const [estadoServicio, setEstadoServicio] = useState<EstadoServicio | null>(null);
  const [estadoAvatar, setEstadoAvatar] = useState<EstadoAvatar>('inactivo');
  /**
   * Presencia elegida en settings.ini: `onda` (grafico de sonido, sin GPU) o
   * `cara` (retrato MuseTalk). Por defecto la onda mientras no se sepa: es la
   * que siempre funciona, y no deja el hueco vacio mientras carga la config.
   */
  const [modoPresencia, setModoPresencia] = useState<'onda' | 'cara'>('onda');
  /**
   * Panel de ajustes de la IA abierto. Es una CAPA encima: la conversacion y el
   * dictado siguen montados debajo, asi que abrir los ajustes no corta el socket
   * de voz ni pierde lo que se estuviera dictando.
   */
  const [ajustesAbiertos, setAjustesAbiertos] = useState(false);

  /**
   * Tamano de la interfaz. Vive en ESTE navegador, no en el servidor: el
   * kiosco del despacho y el portatil de Ricardo abren la misma aplicacion con
   * la misma cuenta, y encogerla aqui no puede encogersela a su padre alli.
   * Ver `lib/escala.ts`.
   */
  const [escala, setEscala] = useState<Escala>(() => leerEscala());
  const tamanoActual = escalaEfectiva(escala);

  const cambiarEscala = useCallback((nueva: Escala) => {
    setEscala(nueva);
    guardarEscala(nueva);
    aplicarEscala(nueva);
  }, []);

  const [sesion, setSesion] = useState<SesionVoz | null>(null);
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [pensando, setPensando] = useState(false);

  // --- Voz conversacional (seccion «Hoy», canal PRINCIPAL) ---
  const [convEscuchando, setConvEscuchando] = useState(false);
  const [convNivel, setConvNivel] = useState(0);
  const [convParcial, setConvParcial] = useState('');
  const [convAviso, setConvAviso] = useState<audio.MensajeAmable | null>(null);
  /**
   * La charla se ha dado por terminada (boton «Finalizar conversación» o
   * dicho en voz alta). El socket se cierra a continuacion POR DISENO, asi
   * que este estado sirve para dos cosas: pintar el cierre con calma y no
   * confundir ese cierre con una caida de linea.
   */
  const [convTerminada, setConvTerminada] = useState(false);
  const [vozSilenciada, setVozSilenciada] = useState(false);
  const [agenteHablando, setAgenteHablando] = useState(false);
  /** true si este equipo puede grabar (se calcula una vez, sin pedir permiso). */
  const [soportaVoz] = useState(() => audio.capturaSoportada());
  /**
   * Canal de conversacion vivo (voz + texto de la seccion «Hoy»). En una ref
   * para que los turnos escritos lo usen sin que su cambio repinte nada.
   */
  const canalConversacion = useRef<ControlConversacion | null>(null);
  /**
   * Contador de reconexion del canal de conversacion. Bumpearlo re-ejecuta el
   * efecto que abre el WebSocket (cierra el viejo y abre uno nuevo). Hace falta
   * porque el movil SUSPENDE la pestana al pasar a segundo plano (el jefe se va
   * a otra app y vuelve): el socket muere sin que cambie usuario/sesion/seccion,
   * asi que sin esto el canal quedaba muerto y "no procesaba nada" al volver
   * (reporte 26/07/2026). Se bumpea al volver a primer plano y al pulsar Hablar.
   */
  /**
   * Espejo de `convTerminada` para los callbacks del WebSocket: se crean al
   * abrir el socket y capturarian el valor viejo del estado.
   */
  const terminadaRef = useRef(false);
  /**
   * Reintentos seguidos de reconexion del canal de voz. Se pone a cero en
   * cuanto llega cualquier mensaje del socket (senal de que hay linea).
   */
  const reintentosRef = useRef(0);
  /** Temporizador del proximo reintento, para poder cancelarlo al desmontar. */
  const temporizadorReconexionRef = useRef<number | null>(null);
  const [convReconexion, setConvReconexion] = useState(0);
  const reconectarConversacion = useCallback(() => setConvReconexion((n) => n + 1), []);

  const [parte, setParte] = useState<ParteDia | null>(null);
  const [posicion, setPosicion] = useState<Posicion | null>(null);
  const [curva, setCurva] = useState<CurvaPrevision | null>(null);
  const [confirmaciones, setConfirmaciones] = useState<Confirmacion[]>([]);
  const [errorPosicion, setErrorPosicion] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);

  const [dictadoTexto, setDictadoTexto] = useState('');
  const [dictadoParcial, setDictadoParcial] = useState('');
  const [dictadoEscuchando, setDictadoEscuchando] = useState(false);
  const [dictadoNivel, setDictadoNivel] = useState(0);
  const [dictadoAviso, setDictadoAviso] = useState<audio.MensajeAmable | null>(null);
  /**
   * Canal de dictado vivo. En una ref y no en el estado: lo usan los botones
   * (escribir a mano) sin que su cambio tenga que repintar nada.
   */
  const canalDictado = useRef<ControlConversacion | null>(null);
  /**
   * Espera del cierre de dictado. El backend guarda la nota en la boveda y
   * contesta por el socket con la ruta real y las dos versiones (la pulida la
   * calcula el, sin modelo). Esperar ese evento evita pedirle al modelo un
   * pulido que ya viene gratis y hecho.
   */
  const cierreDictadoPendiente = useRef<((r: ResultadoDictado | undefined) => void) | null>(
    null,
  );

  useEffect(() => {
    terminadaRef.current = convTerminada;
  }, [convTerminada]);

  /** Recarga los datos de solo lectura. Los fallos no tumban la pantalla. */
  const recargar = useCallback(async () => {
    setCargando(true);
    const [e, p, c, cu, cf] = await Promise.allSettled([
      api.estado(),
      api.parteDelDia(),
      api.posicion(),
      api.prevision(91),
      api.confirmacionesPendientes(),
    ]);
    if (e.status === 'fulfilled') setEstadoServicio(e.value);
    if (p.status === 'fulfilled') setParte(p.value);
    if (c.status === 'fulfilled') {
      setPosicion(c.value);
      setErrorPosicion(null);
    } else {
      setErrorPosicion(c.reason instanceof Error ? c.reason.message : 'Servicio no disponible');
    }
    if (cu.status === 'fulfilled') setCurva(cu.value);
    if (cf.status === 'fulfilled') setConfirmaciones(cf.value);
    setCargando(false);
  }, []);

  /**
   * El microfono abierto tambien es un estado de presencia. La cara lo
   * ignoraba (solo pintaba hablando/pensando), pero la onda TIENE que
   * reaccionar al nivel del micro: es la senal de "le escucho".
   */
  useEffect(() => {
    const escuchando = seccion === 'dictar' ? dictadoEscuchando : convEscuchando;
    setEstadoAvatar((previo) => {
      if (escuchando) return 'escuchando';
      // No se pisa lo que ya esta pasando (hablar o pensar manda).
      return previo === 'escuchando' ? 'inactivo' : previo;
    });
  }, [convEscuchando, dictadoEscuchando, seccion]);

  // Que presencia toca pintar. Si la consulta falla se queda la onda, que no
  // depende de ningun servicio aparte.
  useEffect(() => {
    if (!usuario) return;
    let vivo = true;
    api
      .configAvatar()
      .then((c) => {
        if (vivo) setModoPresencia(c.modo === 'cara' ? 'cara' : 'onda');
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [usuario]);

  /** Pregunta al backend que pide este equipo para entrar (PIN o contrasena). */
  const consultarAcceso = useCallback(async () => {
    try {
      const modo = await api.modoAcceso();
      setAcceso(modo);
      setUsuario(modo.autenticado ? modo.usuario : null);
    } catch {
      // Sin backend no se puede entrar; se pide contrasena por defecto.
      setAcceso({ modo: 'remoto', alias: null, autenticado: false, usuario: null });
      setUsuario(null);
    }
  }, []);

  useEffect(() => {
    void consultarAcceso();
  }, [consultarAcceso]);

  /** Cierra la sesion a peticion del usuario (el equipo sigue siendo kiosco). */
  const salir = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // Aunque el backend no conteste, aqui dentro ya no hay sesion.
    }
    setUsuario(null);
    setSesionCaducada(false);
    void consultarAcceso();
  }, [consultarAcceso]);

  /** Cualquier 401 del backend devuelve al kiosco a la pantalla de entrada.
   *
   * Ademas RECARGA la pagina una sola vez. Motivo real (04/08/2026): el jefe
   * tenia la pestana abierta desde hacia dias; al caducar la sesion, el kiosco
   * de debajo seguia con sus temporizadores y sockets vivos disparando 401 sin
   * parar, y la pantalla se quedaba "atascada" con codigo antiguo en memoria.
   * Recargar deja el kiosco limpio y trae la version publicada.
   *
   * La marca en `sessionStorage` es el freno: sin ella, un 401 tras la recarga
   * (el kiosco pregunta antes de que nadie entre) recargaria en bucle.
   */
  useEffect(() => {
    alCaducarSesion(() => {
      setUsuario(null);
      setSesionCaducada(true);
      try {
        if (!sessionStorage.getItem(MARCA_RECARGA_CADUCIDAD)) {
          sessionStorage.setItem(MARCA_RECARGA_CADUCIDAD, '1');
          window.location.reload();
        }
      } catch {
        // Navegador sin sessionStorage: mejor no recargar que arriesgar bucle.
      }
    });
    return () => alCaducarSesion(null);
  }, []);

  useEffect(() => {
    if (!usuario) return;
    void recargar();
    // El kiosco esta encendido todo el dia: refresco tranquilo cada 5 minutos.
    const id = window.setInterval(() => void recargar(), 5 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [recargar, usuario]);

  /** Abre sesion de conversacion al entrar; una sola por arranque de kiosco. */
  useEffect(() => {
    if (!usuario) return;
    let vivo = true;
    api
      .abrirSesion('conversacion')
      .then((s) => vivo && setSesion(s))
      .catch(() => undefined); // Sin sesion se sigue pudiendo consultar.
    return () => {
      vivo = false;
    };
  }, [usuario]);

  /**
   * Avisos del reproductor de voz hacia la interfaz: mientras suena la voz de
   * Androcles, el avatar "habla" y se enciende el indicador; al callar, vuelve.
   */
  useEffect(() => {
    reproductor.configurarReproductor({
      alHablar: () => {
        setAgenteHablando(true);
        setEstadoAvatar('hablando');
      },
      alCallar: () => {
        setAgenteHablando(false);
        setEstadoAvatar((prev) => (prev === 'hablando' ? 'inactivo' : prev));
      },
    });
    return () => {
      reproductor.pararVoz();
    };
  }, []);

  /**
   * Canal de conversacion de la seccion «Hoy»: un WebSocket abierto mientras se
   * este en esa seccion. Por el viajan tanto la voz (audio del microfono) como
   * el texto escrito, y por el vuelven la transcripcion, la respuesta y su VOZ
   * (WAV que se encola en el reproductor). Abrirlo aqui —y no solo al pulsar el
   * microfono— hace que tambien las preguntas escritas se contesten hablando.
   */
  useEffect(() => {
    if (!usuario || !sesion || seccion !== 'hoy') return;

    let vivo = true;

    const control = conectarConversacion(
      sesion.id,
      (mensaje) => {
        if (!vivo) return;
        // Llega algo: hay linea. Se olvidan los reintentos y se retira el
        // aviso de caida si estaba puesto.
        reintentosRef.current = 0;
        setConvAviso((previo) => (previo?.esDeLinea ? null : previo));
        switch (mensaje.tipo) {
          case 'transcripcion_parcial':
            setConvParcial(mensaje.texto);
            break;
          case 'transcripcion_final':
            // Turno del jefe (venga de la voz o de un texto enviado por el
            // socket): el backend lo devuelve como `transcripcion`.
            setConvParcial('');
            setTurnos((prev) => [
              ...prev,
              {
                id: `voz-j-${Date.now()}`,
                orden: prev.length + 1,
                rol: 'jefe',
                texto: mensaje.texto,
                ts: new Date().toISOString(),
              },
            ]);
            break;
          case 'estado_agente':
            setPensando(mensaje.estado === 'pensando');
            if (mensaje.estado === 'pensando') setEstadoAvatar('pensando');
            break;
          case 'respuesta_parcial':
            // Androcles habla mientras piensa: cada frase que dice aparece ya
            // en el hilo, en vez de esperar 5-20 s a la respuesta entera. Se
            // pinta como turno PARCIAL y se sustituye al llegar el definitivo.
            setPensando(false);
            setTurnos((prev) => {
              const ultimo = prev[prev.length - 1];
              if (ultimo?.parcial && ultimo.rol === 'agente') {
                const fundido = { ...ultimo, texto: `${ultimo.texto} ${mensaje.texto}`.trim() };
                return [...prev.slice(0, -1), fundido];
              }
              return [
                ...prev,
                {
                  id: `voz-a-${Date.now()}`,
                  orden: prev.length + 1,
                  rol: 'agente' as const,
                  texto: mensaje.texto,
                  ts: new Date().toISOString(),
                  parcial: true,
                },
              ];
            });
            break;
          case 'respuesta_final':
            setPensando(false);
            // Sustituye al parcial que se fue pintando frase a frase.
            setTurnos((prev) => {
              const ultimo = prev[prev.length - 1];
              const base = ultimo?.parcial && ultimo.rol === 'agente' ? prev.slice(0, -1) : prev;
              return [...base, mensaje.turno];
            });
            break;
          case 'audio_respuesta':
            // La voz de la respuesta: se encola para sonar en orden. Si el jefe
            // silencio, el reproductor lo descarta solo.
            reproductor.encolarAudio(mensaje.wav);
            break;
          case 'fin_conversacion':
            // Cierre pedido: se apaga el microfono y se deja constancia en el
            // hilo. El `alCerrar` que viene detras NO debe pintar aviso de
            // caida: para eso esta `convTerminada`.
            setConvTerminada(true);
            setConvEscuchando(false);
            setPensando(false);
            setConvParcial('');
            setTurnos((prev) => [
              ...prev,
              {
                id: `fin-${prev.length + 1}`,
                orden: prev.length + 1,
                rol: 'sistema',
                texto: 'Conversación terminada.',
                ts: new Date().toISOString(),
              },
            ]);
            break;
          case 'error': {
            // Un fallo de RED no es "no le he entendido": mandar al jefe a
            // repetir mas despacio cuando lo que se ha caido es el socket le
            // hace pensar que habla mal. El texto se elige por el contenido.
            const esConexion = /conexi|linea|socket|servidor/i.test(mensaje.mensaje);
            setConvAviso(
              esConexion
                ? {
                    titulo: 'Un momento, que recupero la línea.',
                    consejo: 'Sigo aquí; en unos segundos vuelvo a escucharle.',
                    esDeLinea: true,
                  }
                : {
                    titulo: 'No he entendido lo último.',
                    consejo:
                      'Repítalo un poco más despacio, por favor. ' +
                      'Si sigue sin salir, escríbamelo aquí abajo y le contesto igual.',
                  },
            );
            setPensando(false);
            break;
          }
          default:
            break;
        }
      },
      () => {
        if (!vivo) return;
        setConvEscuchando(false);
        // Cierre pedido por el jefe: el socket se cierra a proposito, no es
        // una caida. Sin esta guarda, finalizar la charla pintaba una alarma.
        if (terminadaRef.current) return;

        // Caida de verdad: se RECONECTA SOLO antes de dar la lata. El backend
        // se reinicia (despliegues) y el movil suspende la pestana; hasta
        // ahora cada corte dejaba el aviso rojo puesto hasta que el jefe
        // pulsaba algo (reporte 04/08/2026: "me sale muy a menudo").
        const intento = reintentosRef.current + 1;
        reintentosRef.current = intento;

        if (intento <= MAX_REINTENTOS_LINEA) {
          setConvAviso({
            titulo: 'Un momento, que recupero la línea.',
            consejo: 'Sigo aquí; en unos segundos vuelvo a escucharle.',
            esDeLinea: true,
          });
          if (temporizadorReconexionRef.current) {
            window.clearTimeout(temporizadorReconexionRef.current);
          }
          // Espera creciente (1 s, 2 s, 4 s...) para no martillear un backend
          // que todavia esta arrancando.
          const espera = Math.min(1000 * 2 ** (intento - 1), 15000);
          temporizadorReconexionRef.current = window.setTimeout(() => {
            reconectarConversacion();
          }, espera);
          return;
        }

        setConvAviso({
          titulo: 'Me he quedado sin línea con el servicio.',
          consejo:
            'Pruebe otra vez con «Hablar con Androcles»; si sigo sin oirle, ' +
            'escribame aqui abajo y avise a Ricardo.',
          esDeLinea: true,
        });
      },
      { modo: 'conversacion' },
    );
    canalConversacion.current = control;

    return () => {
      vivo = false;
      if (temporizadorReconexionRef.current) {
        window.clearTimeout(temporizadorReconexionRef.current);
        temporizadorReconexionRef.current = null;
      }
      audio.detenerCaptura();
      reproductor.pararVoz();
      control.cerrar();
      canalConversacion.current = null;
      setConvParcial('');
      setConvNivel(0);
    };
    // `convReconexion` en las deps: al bumpearlo (volver a primer plano tras
    // que el movil suspendiera la pestana, o al pulsar Hablar con el socket
    // muerto) se cierra el socket viejo y se abre uno nuevo.
  }, [usuario, sesion, seccion, convReconexion]);

  /**
   * Auto-reconexion del canal de conversacion al volver a primer plano.
   *
   * El movil suspende la pestana en segundo plano y el WebSocket muere en
   * silencio; al volver, el socket esta cerrado pero ningun estado cambio, asi
   * que el efecto de arriba no se reejecuta solo. Aqui se escucha el regreso a
   * primer plano (visibilidad, foco, reconexion de red) y, si estamos en «Hoy»
   * con sesion y el socket NO esta abierto, se fuerza la reconexion. Solo en
   * ese caso, para no reabrir sin motivo.
   */
  useEffect(() => {
    if (!usuario || !sesion || seccion !== 'hoy') return;
    const alVolver = () => {
      if (document.visibilityState !== 'visible') return;
      if (!canalConversacion.current?.estaAbierto()) reconectarConversacion();
    };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);
    window.addEventListener('online', alVolver);
    return () => {
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
      window.removeEventListener('online', alVolver);
    };
  }, [usuario, sesion, seccion, reconectarConversacion]);

  /**
   * Microfono de la conversacion. Solo se abre mientras el jefe tiene la
   * escucha encendida y esta en «Hoy». El audio sale como PCM 16 kHz por el
   * canal de conversacion ya abierto; el turno lo cierra el silencio detectado
   * en `lib/audio.ts`, no un boton: al jefe le basta con hablar y callarse.
   */
  useEffect(() => {
    if (!convEscuchando || seccion !== 'hoy') return;
    const control = canalConversacion.current;
    if (!control) return;

    let vivo = true;

    // Al hablar, se calla la voz de Androcles (no tiene sentido pisarse).
    reproductor.pararVoz();

    void audio
      .iniciarCaptura(
        (pcm) => control.enviarAudio(pcm),
        () => control.finTurno(),
        {
          alNivel: (n) => {
            if (vivo) setConvNivel(n);
          },
          alFallo: (fallo) => {
            if (!vivo) return;
            setConvAviso({ titulo: fallo.titulo, consejo: fallo.consejo });
            setConvEscuchando(false);
          },
        },
      )
      .then(() => {
        if (vivo) setConvAviso(null);
      })
      .catch((error: unknown) => {
        if (!vivo) return;
        const fallo =
          error instanceof audio.ErrorAudio ? error : new audio.ErrorAudio('desconocido', error);
        setConvAviso({ titulo: fallo.titulo, consejo: fallo.consejo });
        setConvEscuchando(false);
      });

    return () => {
      vivo = false;
      audio.detenerCaptura();
      setConvNivel(0);
    };
  }, [convEscuchando, seccion]);

  /**
   * Canal de dictado: microfono + WebSocket del modo secretaria.
   *
   * Solo se abre mientras el microfono esta activo y estamos en la seccion de
   * dictado. Nada mas conectar se manda `dictado/abrir`: eso pone al backend en
   * estado DICTANDO y es lo que garantiza que el orquestador NO ve el dictado
   * (especificacion 4.4; el aislamiento no depende del prompt).
   *
   * El audio sale como PCM 16 kHz binario por este mismo socket, y el turno lo
   * cierra el silencio detectado en `lib/audio.ts`, no un boton: el usuario no
   * tiene que hacer nada mas que hablar y callarse.
   */
  useEffect(() => {
    if (!sesion || !dictadoEscuchando || seccion !== 'dictar') return;

    let vivo = true;

    const control = conectarConversacion(
      sesion.id,
      (mensaje) => {
        if (!vivo) return;
        switch (mensaje.tipo) {
          case 'transcripcion_parcial':
            setDictadoParcial(mensaje.texto);
            break;
          case 'transcripcion_final':
            // Provisional: el eco `dictado` que viene detras trae el buffer ya
            // editado por los comandos de voz y sustituye a esto.
            setDictadoParcial('');
            setDictadoTexto((prev) => (prev ? `${prev} ${mensaje.texto}` : mensaje.texto));
            break;
          case 'dictado':
            setDictadoParcial('');
            if (mensaje.textoActual !== null) setDictadoTexto(mensaje.textoActual);
            if (mensaje.comando === 'cerrado') {
              // La nota YA esta escrita en boveda\dictados\: esto solo trae la
              // ruta real y las dos versiones para enseñarlas.
              const resolver = cierreDictadoPendiente.current;
              cierreDictadoPendiente.current = null;
              resolver?.({
                slug: '',
                ruta_nota: mensaje.rutaBoveda ?? '',
                texto_literal: mensaje.textoLiteral ?? '',
                texto_pulido: mensaje.textoPulido,
                ruta_docx: null,
              });
            }
            break;
          case 'error':
            // La transcripcion ha fallado, pero el kiosco sigue escribiendo a
            // mano: se avisa sin apagar el microfono.
            setDictadoAviso({
              titulo: 'No he entendido lo último.',
              consejo:
                'Repítalo un poco más despacio, por favor. ' +
                'Si sigue sin salir, escríbamelo aquí abajo y lo apunto igual.',
            });
            break;
          default:
            break;
        }
      },
      () => {
        // Solo llega aqui si el socket muere SOLO (backend caido, sesion
        // rechazada en el handshake). El cierre ordenado de la limpieza pone
        // antes `vivo = false`, asi que no dispara ningun aviso falso.
        if (!vivo) return;
        setDictadoAviso({
          titulo: 'Me he quedado sin línea con el servicio.',
          consejo:
            'Lo que lleva dictado sigue aqui en pantalla, no se ha perdido. ' +
            'Pruebe otra vez con «Empezar a dictar»; si sigo sin oirle, ' +
            'escríbamelo aquí abajo y avise a Ricardo.',
        });
        setDictadoEscuchando(false);
      },
      { modo: 'secretaria', alAbrir: (c) => c.abrirDictado() },
    );
    canalDictado.current = control;

    void audio
      .iniciarCaptura(
        (pcm) => control.enviarAudio(pcm),
        () => control.finTurno(),
        {
          alNivel: (n) => {
            if (vivo) setDictadoNivel(n);
          },
          alFallo: (fallo) => {
            if (!vivo) return;
            setDictadoAviso({ titulo: fallo.titulo, consejo: fallo.consejo });
            setDictadoEscuchando(false);
          },
        },
      )
      .then(() => {
        if (vivo) setDictadoAviso(null);
      })
      .catch((error: unknown) => {
        if (!vivo) return;
        const fallo =
          error instanceof audio.ErrorAudio ? error : new audio.ErrorAudio('desconocido', error);
        setDictadoAviso({ titulo: fallo.titulo, consejo: fallo.consejo });
        // Se apaga el boton, pero el dictado sigue vivo por teclado.
        setDictadoEscuchando(false);
      });

    return () => {
      vivo = false;
      audio.detenerCaptura();
      control.cerrar();
      canalDictado.current = null;
      setDictadoParcial('');
      setDictadoNivel(0);
    };
  }, [sesion, dictadoEscuchando, seccion]);

  /**
   * Anade al dictado un texto escrito por teclado.
   *
   * Si el canal esta vivo se manda como turno de texto para que el buffer del
   * backend siga siendo la unica version buena (y aplique los comandos de
   * edicion). Si no lo esta —microfono caido, backend caido— se acumula en
   * local: el kiosco nunca se queda sin poder tomar nota.
   */
  const escribirDictadoAMano = useCallback((texto: string) => {
    const control = canalDictado.current;
    if (control?.estaAbierto() && control.enviarTexto(texto)) return;
    setDictadoTexto((prev) => (prev ? `${prev} ${texto}` : texto));
  }, []);

  /** Enciende el microfono, o CIERRA el turno para que Androcles conteste.
   *
   * Pulsar cuando NO escucha: empieza a oir.
   * Pulsar cuando SI escucha: cierra el turno con lo que se ha oido y deja de
   * escuchar, para que el agente conteste YA. Esto es imprescindible: con ruido
   * de fondo la deteccion de silencio no salta nunca y, sin este boton, el turno
   * no terminaba y Androcles no llegaba a contestar (reporte del jefe 25/07/2026).
   */
  /**
   * Da la charla por terminada: despedida hablada, cierre de la fila de
   * `sesiones_voz` y microfono apagado. Es la version por boton de decirle
   * "finalizar conversación"; ambas acaban en el mismo sitio del backend.
   */
  const finalizarConversacion = useCallback(() => {
    const control = canalConversacion.current;
    audio.detenerCaptura();
    setConvEscuchando(false);
    if (control?.estaAbierto()) {
      control.finalizar();
    } else {
      // Sin socket vivo no hay a quien pedirselo: se cierra en local.
      setConvTerminada(true);
    }
  }, []);

  /** Reanuda tras haber terminado: socket nuevo y hilo limpio. */
  const reanudarConversacion = useCallback(() => {
    setConvTerminada(false);
    setConvAviso(null);
    setTurnos([]);
    reconectarConversacion();
  }, [reconectarConversacion]);

  const alternarEscuchaConversacion = useCallback(() => {
    // Desbloqueo del audio en el gesto: al pulsar, el jefe autoriza el sonido.
    // Sin esto, la respuesta hablada que llega despues no suena en movil (el
    // navegador ya no considera vivo el gesto). Ver reproductor.desbloquear().
    reproductor.desbloquear();
    setConvAviso(null);
    if (convTerminada) {
      // El jefe vuelve a hablar tras cerrar: se levanta la linea otra vez y
      // se sale (el socket nuevo tarda un instante en estar listo).
      reanudarConversacion();
      setConvEscuchando(true);
      return;
    }
    if (convEscuchando) {
      // Estaba escuchando: el jefe pide que conteste. Se cierra el turno con el
      // audio acumulado (aunque el ruido de fondo no dejara detectar el silencio)
      // y se deja de escuchar. El `finTurno` va como efecto normal (NO dentro del
      // updater de estado, que debe ser puro: ahi no se disparaba fiable y el
      // agente no llegaba a contestar. Reporte del jefe 25/07/2026).
      canalConversacion.current?.finTurno();
      setConvEscuchando(false);
    } else {
      // Si el socket murio en segundo plano (el jefe se fue a otra app y
      // volvio), se reabre ANTES de empezar a oir: el audio que se capture se
      // encola mientras conecta y entra en cuanto el socket manda `iniciar`.
      // Sin esto, se hablaba contra un socket muerto y "no procesaba nada".
      if (!canalConversacion.current?.estaAbierto()) reconectarConversacion();
      setConvEscuchando(true);
    }
  }, [convEscuchando, convTerminada, reanudarConversacion, reconectarConversacion]);

  /** Silencia o devuelve la voz de Androcles (las respuestas siguen en texto). */
  const alternarSilencioVoz = useCallback(() => {
    setVozSilenciada((v) => {
      const nuevo = !v;
      reproductor.ponerSilencio(nuevo);
      return nuevo;
    });
  }, []);

  /**
   * Lee el parte del dia en voz alta. Sintetiza `parte.resumen_hablado` por REST
   * (sin pasar por el agente) y lo encola en el mismo reproductor de la voz.
   */
  const leerParteEnVozAlta = useCallback(async () => {
    if (!parte?.resumen_hablado) return;
    // Desbloqueo del audio DENTRO del gesto (sin await): en movil, si el
    // AudioContext no se reanuda aqui mismo, la sintesis que llega despues del
    // fetch no suena. Ver reproductor.desbloquear().
    reproductor.desbloquear();
    if (vozSilenciada) {
      // El jefe pidio leer: se le devuelve la voz aunque estuviera callada.
      setVozSilenciada(false);
      reproductor.ponerSilencio(false);
    }
    try {
      const wav = await api.sintetizarVoz(parte.resumen_hablado);
      reproductor.encolarAudio(wav);
    } catch {
      setConvAviso({
        titulo: 'Ahora mismo no puedo leerlo en voz alta.',
        consejo: 'Puede leerlo aqui en pantalla; mas tarde vuelva a probar el boton.',
      });
    }
  }, [parte, vozSilenciada]);

  /** Envia un turno escrito y pinta la respuesta del agente. */
  const enviarTexto = useCallback(
    async (texto: string) => {
      // Camino principal: el canal de conversacion esta vivo. El texto viaja
      // por el socket y vuelve por los mismos eventos que la voz (transcripcion
      // + respuesta + WAV), asi que tambien la pregunta escrita se contesta
      // hablando. El turno del jefe lo pinta el eco `transcripcion`, no se anade
      // aqui para no duplicarlo.
      const canal = canalConversacion.current;
      if (canal?.estaAbierto() && canal.enviarTexto(texto)) {
        setPensando(true);
        setEstadoAvatar('pensando');
        return;
      }

      // Degradado: sin socket, se responde por REST (sin voz).
      const provisional: Turno = {
        id: `local-${Date.now()}`,
        orden: turnos.length + 1,
        rol: 'jefe',
        texto,
        ts: new Date().toISOString(),
      };
      setTurnos((prev) => [...prev, provisional]);
      if (!sesion) return;

      setPensando(true);
      setEstadoAvatar('pensando');
      try {
        const respuesta = await api.enviarTexto(sesion.id, texto);
        setTurnos((prev) => [...prev, respuesta]);
        setEstadoAvatar('hablando');
      } catch (e) {
        setTurnos((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            orden: prev.length + 1,
            rol: 'sistema',
            texto:
              e instanceof Error ? `No he podido responderle: ${e.message}` : 'No he podido responderle.',
            ts: new Date().toISOString(),
          },
        ]);
        setEstadoAvatar('inactivo');
      } finally {
        setPensando(false);
      }
    },
    [sesion, turnos.length],
  );

  /** Resuelve una propuesta de Nivel 2 y refresca la bandeja. */
  const resolverConfirmacion = useCallback(
    async (id: number, decision: 'confirmada' | 'rechazada') => {
      await api.resolverConfirmacion(id, decision, sesion?.id);
      setConfirmaciones(await api.confirmacionesPendientes());
    },
    [sesion],
  );

  /** Cierra el dictado en el backend (nota en boveda, docx opcional). */
  const cerrarDictado = useCallback(
    async (exportarWord: boolean) => {
      if (!sesion) return;
      const control = canalDictado.current;

      // Camino normal: el socket esta vivo. Cerrar por ahi es lo que saca al
      // backend de modo secretaria Y deja la nota en boveda\dictados\ con las
      // dos versiones. La pulida la calcula el backend sin modelo, asi que no
      // hay que gastar ni un token pidiendosela otra vez por REST.
      if (control?.estaAbierto()) {
        const resultado = await new Promise<ResultadoDictado | undefined>((resolver) => {
          cierreDictadoPendiente.current = resolver;
          control.cerrarDictado();
          // Si el backend no contesta, el kiosco no se queda con el boton
          // «Guardando…» puesto para siempre.
          window.setTimeout(() => {
            if (cierreDictadoPendiente.current === resolver) {
              cierreDictadoPendiente.current = null;
              resolver(undefined);
            }
          }, 5_000);
        });
        if (resultado) {
          setDictadoEscuchando(false);
          setDictadoAviso(null);
          // El Word es un paso APARTE del cierre: el socket deja la nota en la
          // boveda, pero el `.docx` lo genera su endpoint. Sin esto, «Guardar y
          // hacer Word» no hacia nada por el camino normal (el de socket vivo).
          if (!exportarWord) return resultado;
          const ruta = await api.exportarDictadoWord(
            resultado.texto_literal || dictadoTexto,
          );
          return { ...resultado, ruta_docx: ruta };
        }
      }

      // Degradado: microfono apagado o socket caido. Aqui no hay nota en la
      // boveda, asi que al menos se devuelve el texto pulido.
      // TODO(Fase4): endpoint REST que guarde en la boveda un dictado escrito
      // a mano, sin pasar por el socket de voz.
      const resultado = await api.cerrarDictado(sesion.id, dictadoTexto, exportarWord);
      setDictadoEscuchando(false);
      setDictadoAviso(null);
      return resultado;
    },
    [sesion, dictadoTexto],
  );

  return (
    <div className="relative min-h-full lg:h-full">
    {/*
      Movil: flujo natural, la PAGINA hace scroll. Escritorio (lg): dos columnas
      que llenan el alto del viewport y hacen scroll cada una por su lado.
      Antes se forzaba `h-full` + `flex-1` + `overflow` tambien en movil, y como
      el avatar ocupaba ~420px al contenido le quedaban ~110px con scroll
      interno: el parte y el chat quedaban invisibles (reporte del jefe).
    */}
    <div className="mx-auto flex min-h-full max-w-[112rem] flex-col gap-5 p-5 lg:h-full lg:flex-row lg:gap-6 lg:p-6" aria-hidden={!usuario || ajustesAbiertos}>
      {/* Columna izquierda: presencia + DIALOGO. Los botones de hablar van
          DEBAJO del avatar (peticion del jefe 25/07/2026): la cara y el "Hablar
          con Androcles" forman una unidad, visible en cualquier pestana. */}
      {/* Ancho: 23rem se quedaba corto para la escala del kiosco (texto base
          22px) y todo iba justo. En pantallas grandes se ensancha; la columna
          derecha sigue teniendo de sobra. */}
      {/*
        En «Dictar» la columna izquierda se aparta: el jefe pidio que la hoja
        ocupe practicamente toda la pantalla (04/08/2026). El avatar y la
        conversacion siguen MONTADOS (no se desmontan: el socket de voz y el
        dictado a medias sobreviven al cambio de pestana), solo ocultos.
      */}
      <aside
        className={cn(
          'flex w-full shrink-0 flex-col gap-4 lg:min-h-0 lg:w-[24rem] lg:overflow-y-auto lg:pr-1 xl:w-[28rem]',
          seccion === 'dictar' && 'hidden',
        )}
      >
        {/* Reparto de alto en escritorio: el retrato y la barra de estado tienen
            tamano propio (`shrink-0`) y la conversacion se queda con lo que
            sobra y scrollea por dentro. Sin esto, la columna sumaba mas de lo
            que cabe y el formulario del chat se pintaba ENCIMA de la barra de
            estado (reporte «la version PC esta descuadrada», 29/07/2026). El
            tope del retrato evita que se coma media columna en pantallas altas.
            Y si aun asi no cabe todo (pantallas bajas), la columna entera
            scrollea en vez de solaparse. */}
        {/* Onda o cara, segun `[avatar] modo` de settings.ini. La cara sigue
            entera detras del interruptor: cuando haya GPU para el lip-sync,
            se cambia el valor y vuelve sin tocar codigo. */}
        {modoPresencia === 'cara' ? (
          <Avatar
            estado={estadoAvatar}
            sesionActiva={!!usuario}
            className="shrink-0 lg:max-h-[32vh]"
          />
        ) : (
          <OndaVoz
            estado={estadoAvatar}
            nivel={seccion === 'dictar' ? dictadoNivel : convNivel}
            className="shrink-0"
          />
        )}
        <Conversacion
          // Sin `flex-1` a proposito: forzarle una altura menor que la de sus
          // partes no encogibles (barra de voz + formulario) hacia que estas se
          // salieran de la caja y se pintaran sobre la barra de estado. Aqui la
          // conversacion mide lo que necesita, el hilo de mensajes tiene su
          // propio tope, y si la suma no cabe scrollea la columna entera.
          className="min-h-0 shrink-0"
          turnos={turnos}
          pensando={pensando}
          alEnviarTexto={(t) => void enviarTexto(t)}
          soportaVoz={soportaVoz}
          escuchando={convEscuchando}
          nivel={convNivel}
          hablando={agenteHablando}
          silenciada={vozSilenciada}
          textoParcial={convParcial}
          aviso={convAviso}
          alAlternarEscucha={alternarEscuchaConversacion}
          alAlternarSilencio={alternarSilencioVoz}
          terminada={convTerminada}
          alFinalizar={finalizarConversacion}
          alReanudar={reanudarConversacion}
        />
        <BarraEstado
          estado={estadoServicio}
          usuario={usuario}
          alRecargar={() => void recargar()}
          alSalir={salir}
        />
      </aside>

      {/* Columna derecha: contenido. En movil crece con su contenido (la pagina
          scrollea); en escritorio ocupa el alto y scrollea por dentro. */}
      <main className="flex flex-1 flex-col gap-5 lg:min-h-0">
        {/* Control segmentado: en vez de tres botones a todo ancho (que
            parecian tres losas), un selector compacto con la pestana activa
            resaltada sobre una pista comun. Sigue siendo comodo de tocar. */}
        <nav
          className="flex shrink-0 gap-1 rounded-lg border border-border/70 bg-card/60 p-1.5"
          aria-label="Secciones"
        >
          {SECCIONES.map(({ clave, etiqueta }) => {
            const activa = seccion === clave;
            return (
              <button
                key={clave}
                type="button"
                aria-current={activa ? 'page' : undefined}
                onClick={() => setSeccion(clave)}
                className={
                  'flex-1 whitespace-nowrap rounded-md px-3 py-3 text-base font-medium transition-colors sm:text-lg ' +
                  (activa
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground')
                }
              >
                {etiqueta}
              </button>
            );
          })}

          {/* Ajustes vive AQUI, no en la barra de estado de la izquierda
              (05/08/2026: "no veo ningun menu de setting"). Alli quedaba al
              fondo de una columna con scroll propio, debajo de la onda, los
              botones de hablar y la conversacion entera —habia que bajar hasta
              el final para encontrarlo—, y ademas esa columna se oculta en
              «Dictar». En la fila de pestanas esta siempre a la vista, en
              cualquier seccion. */}
          {/* Tamano de la pantalla, siempre a mano: con el kiosco a tamano
              grande en un portatil no se puede trabajar, y el sitio donde se
              arregla no puede estar detras de tres pantallas. Alterna entre
              grande y normal; las tres opciones (con el automatico) estan en
              Ajustes. */}
          <BotonTamano tamano={tamanoActual} alAlternar={cambiarEscala} />

          {usuario && (
            <button
              type="button"
              onClick={() => setAjustesAbiertos(true)}
              className="shrink-0 whitespace-nowrap rounded-md border border-border px-4 py-3 text-base font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground sm:text-lg"
            >
              Ajustes
            </button>
          )}
        </nav>

        {/* Escritorio: caja con scroll propio. Movil: sin overflow, fluye en la
            pagina para no crear un scroll dentro de otro scroll. */}
        <div className="flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-2">
          {seccion === 'hoy' && (
            <div className="flex flex-col gap-6">
              {/* La conversacion (hablar con Androcles) vive ahora en la columna
                  izquierda, bajo el avatar. Aqui queda el parte y la bandeja. */}
              <ParteDelDia
                parte={parte}
                cargando={cargando}
                alPedirLectura={() => void leerParteEnVozAlta()}
                alIrABandeja={() => setSeccion('hoy')}
              />
              <BandejaConfirmaciones
                confirmaciones={confirmaciones}
                cargando={cargando}
                alResolver={resolverConfirmacion}
              />
            </div>
          )}

          {seccion === 'caja' && (
            <div className="flex flex-col gap-6">
              <PosicionPanel posicion={posicion} cargando={cargando} error={errorPosicion} />
              <PrevisionChart curva={curva} cargando={cargando} />
            </div>
          )}

          {seccion === 'dictar' && (
            <Dictado
              textoFinal={dictadoTexto}
              textoParcial={dictadoParcial}
              escuchando={dictadoEscuchando}
              nivel={dictadoNivel}
              aviso={dictadoAviso}
              alAlternarEscucha={() => {
                // El microfono lo abre y lo cierra el efecto de arriba; aqui
                // solo se cambia la intencion del usuario.
                setDictadoAviso(null);
                setDictadoEscuchando((v) => !v);
              }}
              alEscribirAMano={escribirDictadoAMano}
              alCerrar={cerrarDictado}
              className="h-full"
            />
          )}
        </div>
      </main>
    </div>

      {/*
        Ajustes de la IA COMO CAPA, por el mismo motivo que la pantalla de
        entrada: el kiosco de debajo NO se desmonta, asi que cambiar la voz o el
        cerebro no corta la conversacion ni pierde el dictado a medias.
      */}
      {usuario && ajustesAbiertos && (
        <PanelAjustes
          alCerrar={() => setAjustesAbiertos(false)}
          escala={escala}
          alCambiarEscala={cambiarEscala}
        />
      )}

      {/*
        Pantalla de entrada COMO CAPA: el kiosco de debajo sigue montado, asi
        que al volver a entrar el dictado a medias y la conversacion siguen
        donde estaban.
      */}
      {!usuario && (
        <div className="fixed inset-0 z-50 bg-background">
          <BotonTamano
            tamano={tamanoActual}
            alAlternar={cambiarEscala}
            className="absolute right-4 top-4 z-10 bg-background"
          />
          <Login
            acceso={acceso}
            caducada={sesionCaducada}
            alEntrar={(abierta) => {
              setUsuario(abierta.usuario);
              setSesionCaducada(false);
              // Sesion nueva: la proxima caducidad vuelve a merecer su recarga.
              try {
                sessionStorage.removeItem(MARCA_RECARGA_CADUCIDAD);
              } catch {
                // Sin sessionStorage no hay marca que limpiar.
              }
              void consultarAcceso();
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Barra de estado del servicio.
 * Muestra el kill switch (settings.ini -> agente_activo) porque cambia lo que
 * Androcles puede hacer: con el switch en falso solo consulta, no escribe.
 */
function BarraEstado({
  estado,
  usuario,
  alRecargar,
  alSalir,

}: {
  estado: EstadoServicio | null;
  usuario: UsuarioSesion | null;
  alRecargar: () => void;
  alSalir: () => void;

}) {
  const ok = estado?.ok ?? false;

  return (
    <div className="tarjeta-kiosco flex shrink-0 flex-col gap-4">
      <div className="flex items-center gap-4">
        <span
          className={cn(
            'h-5 w-5 shrink-0 rounded-full',
            ok ? 'bg-[hsl(var(--cobro))]' : 'bg-destructive',
          )}
          aria-hidden="true"
        />
        <span className="text-base font-semibold">
          {ok ? 'Todo en orden' : 'Sin conexion con el servicio'}
        </span>
      </div>

      {estado && !estado.agente_activo && (
        <p className="text-sm text-muted-foreground">
          Modo consulta: no puedo cambiar nada hasta que Ricardo lo active.
        </p>
      )}

      {/* Los dos botones en fila: apilados a lo ancho de la columna se comian
          alto que hace falta para la conversacion, y son de uso ocasional. */}
      <div className="flex gap-3">
        <Boton variante="fantasma" className="flex-1" onClick={alRecargar}>
          Actualizar
        </Boton>
        {usuario && (
          <Boton variante="contorno" className="flex-1" onClick={alSalir}>
            Salir
          </Boton>
        )}
      </div>

      {usuario && (
        <p className="text-sm text-muted-foreground">
          Dentro como <strong className="font-semibold">{usuario.nombre}</strong>.
        </p>
      )}
    </div>
  );
}
